import { Column, Lookup } from 'devextreme-react/data-grid';
import Accordion, { Item } from 'devextreme-react/accordion';
import { SubordinateCollectionEditor } from '../subordinate-collection-editor';
import { useProjectClients } from './use-project-clients';
import { ClientContactsEditor } from './client-contacts-editor';
import { BillingClientDetail } from './billing-client-detail';
import { ProjectEditorApiService } from '~/services';

// Numeric ids mirror Domain.Entities.ProjectRelationship (Billing=1, Construction=2, Landlord=3, Estimating=4).
const PROJECT_RELATIONSHIP = { BILLING: 1, CONSTRUCTION: 2, ESTIMATING: 4 };

// Static reference lookups — module-level so their store identity is stable across renders (WK-526).
const projectEditorApiService = new ProjectEditorApiService();
const landlordContactTypesDataSource = projectEditorApiService.getLandlordContactTypesDataSource();
const landlordCompaniesDataSource = projectEditorApiService.getLandlordCompaniesDataSource();
const constructionCompaniesDataSource = projectEditorApiService.getConstructionCompaniesDataSource();
const billingCompaniesDataSource = projectEditorApiService.getBillingCompaniesDataSource();
const estimatingCompaniesDataSource = projectEditorApiService.getEstimatingCompaniesDataSource();

const accordionTitleStyle = { fontSize: '15px', fontWeight: 600 };
const accordionCountStyle = { fontWeight: 400, color: '#888', marginLeft: '6px' };

// Title shown on each accordion section header: the client-type label plus a live count.
function SectionTitle({ label, count }) {
  return (
    <span style={accordionTitleStyle}>
      {label}
      <span style={accordionCountStyle}>({count})</span>
    </span>
  );
}

// Landlord is singular, so its "Primary" is always true and not editable; company changes swap in
// place (see onUpdateRow -> setLandlordCompany). Columns otherwise mirror the job/billing grids.
const landlordClientGridColumns = [
  <Column key="companyId" dataField="companyId" caption="Name">
    <Lookup dataSource={landlordCompaniesDataSource} displayExpr="displayName" valueExpr="id" />
  </Column>,
  <Column key="primary" dataField="primary" caption="Primary" dataType="boolean" allowEditing={false} />,
];

const jobClientGridColumns = [
  <Column key="companyId" dataField="companyId" caption="Name">
    <Lookup dataSource={constructionCompaniesDataSource} displayExpr="displayName" valueExpr="id" />
  </Column>,
  <Column key="primary" dataField="primary" caption="Primary" dataType="boolean" />,
];

const billingClientGridColumns = [
  <Column key="companyId" dataField="companyId" caption="Name">
    <Lookup dataSource={billingCompaniesDataSource} displayExpr="displayName" valueExpr="id" />
  </Column>,
  <Column key="primary" dataField="primary" caption="Primary" dataType="boolean" />,
];

const estimatingClientGridColumns = [
  <Column key="companyId" dataField="companyId" caption="Name">
    <Lookup dataSource={estimatingCompaniesDataSource} displayExpr="displayName" valueExpr="id" />
  </Column>,
  <Column key="primary" dataField="primary" caption="Primary" dataType="boolean" />,
];

// $values is the API's collection wrapper; bind to it directly rather than normalizing.
const asArray = (collection) => collection?.$values ?? [];

// Read-VM client -> grid row (the grid keys on the ProjectClient row id; the Lookup binds companyId).
const toClientRow = (client) => ({
  projectClientId: client.projectClientId,
  companyId: client.clientId,
  primary: client.primary,
});

// Read-VM client -> ClientContactsEditor seed (carries the row ids needed to target updates/deletes).
const toContactSeed = (client) =>
  asArray(client?.contacts).map((contact) => ({
    contactId: contact.contactId,
    fullName: contact.fullName,
    preferredPhone: contact.preferredPhone,
    preferredEmail: contact.preferredEmail,
    landlordContactTypeId: contact.landlordContactTypeId,
    projectContactId: contact.projectContactId,
  }));

/**
 * Landlord, job, and billing clients (and their contacts) for a project, rendered as collapsible
 * accordion sections. Every change persists immediately via its own API call, like estimate items.
 * Shared by the estimate edit form's Project Contacts tab and the estimate→submitted transition form,
 * so both surfaces show existing data and save identically.
 */
export function ProjectClientsEditor({ projectViewModel }) {
  const projectId = projectViewModel.id;
  const {
    landlord,
    constructionClients,
    billingClients,
    estimatingClients,
    reloadKey,
    setLandlordCompany,
    addClient,
    updateClient,
    deleteClient,
    addContact,
    removeContact,
    changeContactType,
  } = useProjectClients(projectId, projectViewModel);

  const findClient = (clients, projectClientId) =>
    asArray(clients).find((c) => c.projectClientId === projectClientId);

  const renderLandlordDetail = (client) => (
    <ClientContactsEditor
      companyId={client.clientId}
      initialContacts={toContactSeed(client)}
      landlordContactTypeDataSource={landlordContactTypesDataSource}
      onAdd={(contact) =>
        addContact(client.projectClientId, {
          contactId: contact.contactId,
          landlordContactTypeId: contact.landlordContactTypeId,
        })
      }
      onRemove={(contact) => removeContact(client.projectClientId, contact.projectContactId)}
      onTypeChange={(contact, typeId) =>
        changeContactType(client.projectClientId, contact.projectContactId, typeId)
      }
    />
  );

  const renderConstructionDetail = (client) => (
    <ClientContactsEditor
      companyId={client.clientId}
      initialContacts={toContactSeed(client)}
      onAdd={(contact) => addContact(client.projectClientId, { contactId: contact.contactId })}
      onRemove={(contact) => removeContact(client.projectClientId, contact.projectContactId)}
    />
  );

  const renderEstimatingDetail = (client) => (
    <ClientContactsEditor
      companyId={client.clientId}
      initialContacts={toContactSeed(client)}
      onAdd={(contact) => addContact(client.projectClientId, { contactId: contact.contactId })}
      onRemove={(contact) => removeContact(client.projectClientId, contact.projectContactId)}
    />
  );

  const renderBillingDetail = (client) => (
    <BillingClientDetail
      companyId={client.clientId}
      initialContacts={toContactSeed(client)}
      initialBillingAddressId={client.billingAddressId ?? null}
      onContactAdd={(contact) => addContact(client.projectClientId, { contactId: contact.contactId })}
      onContactRemove={(contact) => removeContact(client.projectClientId, contact.projectContactId)}
      onBillingAddressChange={(id) => updateClient(client.projectClientId, { billingAddressId: id })}
    />
  );

  return (
    <Accordion collapsible={true} multiple={true}>
      <Item titleRender={() => <SectionTitle label="Estimate Contacts" count={asArray(estimatingClients).length} />}>
        <SubordinateCollectionEditor
          key={`estimating-${reloadKey}`}
          gridColumns={estimatingClientGridColumns}
          keyExpr="projectClientId"
          noDataText="No estimate contacts added yet."
          confirmDeleteMessage="Are you sure you want to remove this estimating company?"
          initialItems={asArray(estimatingClients).map(toClientRow)}
          autoExpandDetail={true}
          newRowDefaults={{ primary: false }}
          detailRender={(e) => {
            const client = findClient(estimatingClients, e.key);
            return client ? renderEstimatingDetail(client) : null;
          }}
          onCreateRow={(row) =>
            addClient(PROJECT_RELATIONSHIP.ESTIMATING, { companyId: row.companyId, primary: !!row.primary })
          }
          onUpdateRow={(key, changes) => updateClient(key, changes)}
          onDeleteRow={(key) => deleteClient(key)}
        />
      </Item>

      <Item titleRender={() => <SectionTitle label="Landlord" count={landlord ? 1 : 0} />}>
        <SubordinateCollectionEditor
          key={`landlord-${reloadKey}`}
          gridColumns={landlordClientGridColumns}
          keyExpr="projectClientId"
          noDataText="No landlord added yet."
          confirmDeleteMessage="Are you sure you want to remove this landlord?"
          initialItems={landlord ? [toClientRow(landlord)] : []}
          autoExpandDetail={true}
          allowAdding={!landlord}
          allowDeleting={true}
          newRowDefaults={{ primary: true }}
          detailRender={(e) =>
            landlord && landlord.projectClientId === e.key ? renderLandlordDetail(landlord) : null
          }
          onCreateRow={(row) => setLandlordCompany(row.companyId)}
          onUpdateRow={(_key, changes) => {
            // Landlord is singular: a company change swaps it in place (clearing the old company's
            // contacts). Ignore no-op saves that don't actually change the company.
            if (changes.companyId != null && changes.companyId !== landlord?.clientId) {
              setLandlordCompany(changes.companyId);
            }
          }}
          onDeleteRow={(key) => deleteClient(key)}
        />
      </Item>

      <Item titleRender={() => <SectionTitle label="Job Clients" count={asArray(constructionClients).length} />}>
        <SubordinateCollectionEditor
          key={`construction-${reloadKey}`}
          gridColumns={jobClientGridColumns}
          keyExpr="projectClientId"
          noDataText="No job clients added yet."
          confirmDeleteMessage="Are you sure you want to remove this job client?"
          initialItems={asArray(constructionClients).map(toClientRow)}
          autoExpandDetail={true}
          newRowDefaults={{ primary: false }}
          detailRender={(e) => {
            const client = findClient(constructionClients, e.key);
            return client ? renderConstructionDetail(client) : null;
          }}
          onCreateRow={(row) =>
            addClient(PROJECT_RELATIONSHIP.CONSTRUCTION, { companyId: row.companyId, primary: !!row.primary })
          }
          onUpdateRow={(key, changes) => updateClient(key, changes)}
          onDeleteRow={(key) => deleteClient(key)}
        />
      </Item>

      <Item titleRender={() => <SectionTitle label="Billing Clients" count={asArray(billingClients).length} />}>
        <SubordinateCollectionEditor
          key={`billing-${reloadKey}`}
          gridColumns={billingClientGridColumns}
          keyExpr="projectClientId"
          noDataText="No billing clients added yet."
          confirmDeleteMessage="Are you sure you want to remove this billing client?"
          initialItems={asArray(billingClients).map(toClientRow)}
          autoExpandDetail={true}
          newRowDefaults={{ primary: false }}
          detailRender={(e) => {
            const client = findClient(billingClients, e.key);
            return client ? renderBillingDetail(client) : null;
          }}
          onCreateRow={(row) =>
            addClient(PROJECT_RELATIONSHIP.BILLING, { companyId: row.companyId, primary: !!row.primary })
          }
          onUpdateRow={(key, changes) => updateClient(key, changes)}
          onDeleteRow={(key) => deleteClient(key)}
        />
      </Item>
    </Accordion>
  );
}
